/**
 * Phase B — Canonical Tutors Admin Panel.
 *
 * Read-through UI for the tutors_canonical single source of truth.
 * Provides:
 *   • KPI strip (total / approved / portal / directory / accepting-students)
 *   • Action Centre — approved tutors with broken portal access (Fix button)
 *   • Directory publication toggles (Show / Hide + Accepting students)
 *   • Backfill button (dry-run + real)
 *   • Duplicate report (read-only)
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Wrench, Eye, EyeOff, CheckCircle2, AlertTriangle } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

function useAuthedFetch() {
  return useCallback(async (path, opts = {}) => {
    const res = await fetch(`${API}${path}`, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
      ...opts,
    });
    if (!res.ok) {
      const t = await res.text().catch(() => '');
      throw new Error(`${res.status}: ${t || res.statusText}`);
    }
    return res.json();
  }, []);
}

function KpiStrip({ kpis, onReload, reloading }) {
  const cards = [
    { key: 'total', label: 'Canonical tutors' },
    { key: 'approved', label: 'Approved' },
    { key: 'portal_enabled', label: 'Portal enabled' },
    { key: 'directory_visible', label: 'In public directory' },
    { key: 'accepting_students', label: 'Accepting students' },
    { key: 'archived', label: 'Archived orphans' },
    { key: 'approved_no_portal', label: 'Approved · portal broken', warn: true },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
      {cards.map((c) => (
        <div
          key={c.key}
          data-testid={`canon-kpi-${c.key}`}
          className={`rounded-xl border p-4 ${c.warn && kpis[c.key] > 0 ? 'border-amber-500 bg-amber-50' : 'border-slate-200 bg-white'}`}
        >
          <div className="text-xs uppercase tracking-wide text-slate-500">{c.label}</div>
          <div className="text-2xl font-semibold text-slate-900 mt-1">{kpis[c.key] ?? '—'}</div>
        </div>
      ))}
      <div className="col-span-2 md:col-span-4 lg:col-span-7 flex justify-end">
        <Button
          size="sm"
          variant="outline"
          onClick={onReload}
          disabled={reloading}
          data-testid="canon-reload"
        >
          {reloading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
          Refresh
        </Button>
      </div>
    </div>
  );
}

function ActionCentre({ items, onFix, busyEmail }) {
  if (!items.length) {
    return (
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 flex items-center gap-2">
        <CheckCircle2 className="h-5 w-5 text-emerald-600" />
        <div className="text-sm text-emerald-800">All approved tutors have working portal access. Nothing to fix.</div>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-slate-600 border-b border-amber-200">
            <th className="p-3">Tutor</th>
            <th className="p-3">Reason</th>
            <th className="p-3">Portal status</th>
            <th className="p-3 text-right">Action</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.email} className="border-b border-amber-100" data-testid={`canon-action-row-${it.email}`}>
              <td className="p-3">
                <div className="font-medium text-slate-800">{it.full_name || it.email}</div>
                <div className="text-xs text-slate-500">{it.email}</div>
              </td>
              <td className="p-3"><Badge variant="outline">{it.reason}</Badge></td>
              <td className="p-3">
                {it.portal_enabled ? 'active' : 'disabled'}
                {it.portal_invite_status && <span className="text-xs text-slate-500 ml-2">invite: {it.portal_invite_status}</span>}
              </td>
              <td className="p-3 text-right">
                <Button
                  size="sm"
                  onClick={() => onFix(it.email)}
                  disabled={busyEmail === it.email}
                  data-testid={`canon-fix-portal-${it.email}`}
                >
                  {busyEmail === it.email
                    ? <Loader2 className="h-4 w-4 animate-spin mr-1" />
                    : <Wrench className="h-4 w-4 mr-1" />
                  }
                  Fix Portal Access
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TutorsTable({ tutors, onToggleDirectory, busyEmail, query, setQuery }) {
  const filtered = useMemo(() => {
    if (!query) return tutors;
    const q = query.toLowerCase();
    return tutors.filter((t) => (t.email || '').includes(q) || (t.full_name || '').toLowerCase().includes(q));
  }, [tutors, query]);

  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      <div className="p-3 border-b border-slate-100 flex items-center gap-2">
        <Input
          data-testid="canon-search"
          placeholder="Search email or name…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="max-w-sm"
        />
        <div className="text-sm text-slate-500 ml-auto">{filtered.length} of {tutors.length}</div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-600 border-b border-slate-100">
              <th className="p-3">Tutor</th>
              <th className="p-3">Status</th>
              <th className="p-3">Portal</th>
              <th className="p-3">Directory</th>
              <th className="p-3">Accepting</th>
              <th className="p-3">Sources</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((t) => (
              <tr key={t.email} className="border-b border-slate-50" data-testid={`canon-row-${t.email}`}>
                <td className="p-3">
                  <div className="font-medium text-slate-800">{t.full_name || t.email}</div>
                  <div className="text-xs text-slate-500">{t.email}</div>
                  {t.migration_status === 'orphan_archived' && (
                    <Badge variant="outline" className="mt-1 text-amber-700 border-amber-300">
                      <AlertTriangle className="h-3 w-3 mr-1" /> orphan archived
                    </Badge>
                  )}
                </td>
                <td className="p-3">
                  <Badge variant={t.status === 'approved' ? 'default' : 'outline'}>{t.status}</Badge>
                </td>
                <td className="p-3">
                  {t.portal_enabled
                    ? <span className="text-emerald-700">active</span>
                    : <span className="text-slate-400">—</span>}
                </td>
                <td className="p-3">
                  {t.directory_visible
                    ? <span className="text-emerald-700 flex items-center gap-1"><Eye className="h-3 w-3" />published</span>
                    : <span className="text-slate-400 flex items-center gap-1"><EyeOff className="h-3 w-3" />hidden</span>}
                </td>
                <td className="p-3">{t.accepting_students ? 'yes' : 'no'}</td>
                <td className="p-3 text-xs text-slate-500">
                  {t.source_collection}
                </td>
                <td className="p-3 text-right space-x-1">
                  {t.status === 'approved' && (
                    <Button
                      size="sm"
                      variant={t.directory_visible ? 'outline' : 'default'}
                      onClick={() => onToggleDirectory(t.email, !t.directory_visible, t.accepting_students || !t.directory_visible)}
                      disabled={busyEmail === t.email}
                      data-testid={`canon-toggle-directory-${t.email}`}
                    >
                      {t.directory_visible ? 'Hide' : 'Show in directory'}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AdminCanonicalTutorsPanel() {
  const fetchJson = useAuthedFetch();
  const [kpis, setKpis] = useState({});
  const [tutors, setTutors] = useState([]);
  const [action, setAction] = useState([]);
  const [dupes, setDupes] = useState([]);
  const [query, setQuery] = useState('');
  const [reloading, setReloading] = useState(false);
  const [busyEmail, setBusyEmail] = useState(null);
  const [backfillingRun, setBackfillingRun] = useState(null);

  const reload = useCallback(async () => {
    setReloading(true);
    try {
      const [k, l, a, d] = await Promise.all([
        fetchJson('/admin/canonical/tutors/kpis'),
        fetchJson('/admin/canonical/tutors'),
        fetchJson('/admin/canonical/tutors/action-centre'),
        fetchJson('/admin/canonical/tutors/duplicates'),
      ]);
      setKpis(k);
      setTutors(l.items || []);
      setAction(a.items || []);
      setDupes(d.items || []);
    } catch (e) {
      toast.error(`Load failed: ${e.message}`);
    } finally {
      setReloading(false);
    }
  }, [fetchJson]);

  useEffect(() => { reload(); }, [reload]);

  const backfill = async (dry) => {
    setBackfillingRun(dry ? 'dry' : 'real');
    try {
      const r = await fetchJson(`/admin/canonical/tutors/backfill?dry_run=${dry}`, { method: 'POST' });
      const msg = dry
        ? `Dry-run: would insert ${r.inserts}, update ${r.updates}, archive ${r.orphans_archived} orphans`
        : `Backfill done: +${r.inserts} inserted, ${r.updates} updated, ${r.already_current} unchanged, ${r.orphans_archived} orphans archived`;
      toast.success(msg);
      if (!dry) await reload();
    } catch (e) {
      toast.error(`Backfill failed: ${e.message}`);
    } finally {
      setBackfillingRun(null);
    }
  };

  const fixPortal = async (email) => {
    setBusyEmail(email);
    try {
      await fetchJson(`/admin/canonical/tutors/${encodeURIComponent(email)}/fix-portal`, {
        method: 'POST',
        body: JSON.stringify({ resend_invite: true, regenerate_password: false }),
      });
      toast.success(`Portal repaired for ${email}`);
      await reload();
    } catch (e) {
      toast.error(`Fix failed: ${e.message}`);
    } finally {
      setBusyEmail(null);
    }
  };

  const toggleDirectory = async (email, visible, accepting) => {
    setBusyEmail(email);
    try {
      await fetchJson(`/admin/canonical/tutors/${encodeURIComponent(email)}/directory`, {
        method: 'PATCH',
        body: JSON.stringify({ directory_visible: visible, accepting_students: accepting }),
      });
      toast.success(`${email} ${visible ? 'published to' : 'hidden from'} directory`);
      await reload();
    } catch (e) {
      toast.error(`Toggle failed: ${e.message}`);
    } finally {
      setBusyEmail(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="canonical-tutors-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900">Canonical Tutors <span className="text-slate-400 text-sm font-normal">(single source of truth)</span></h2>
          <p className="text-sm text-slate-600 mt-1">
            One row per tutor. Approval status controls portal + assignability. Directory publication is separate.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => backfill(true)}
            disabled={!!backfillingRun}
            data-testid="canon-backfill-dry"
          >
            {backfillingRun === 'dry' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Dry-run backfill
          </Button>
          <Button
            size="sm"
            onClick={() => backfill(false)}
            disabled={!!backfillingRun}
            data-testid="canon-backfill-real"
          >
            {backfillingRun === 'real' ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Run backfill
          </Button>
        </div>
      </div>

      <KpiStrip kpis={kpis} onReload={reload} reloading={reloading} />

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Wrench className="h-4 w-4 text-amber-600" /> Action Centre — Fix Portal Access
            {action.length > 0 && <Badge variant="outline" className="text-amber-700 border-amber-300">{action.length}</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ActionCentre items={action} onFix={fixPortal} busyEmail={busyEmail} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Canonical tutor inventory</CardTitle>
        </CardHeader>
        <CardContent>
          <TutorsTable
            tutors={tutors}
            onToggleDirectory={toggleDirectory}
            busyEmail={busyEmail}
            query={query}
            setQuery={setQuery}
          />
        </CardContent>
      </Card>

      {dupes.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Historical duplicates (report only — no auto-merge)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg border border-slate-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-600 border-b border-slate-100">
                    <th className="p-3">Canonical</th>
                    <th className="p-3">Duplicate refs</th>
                  </tr>
                </thead>
                <tbody>
                  {dupes.map((d) => (
                    <tr key={d.email} className="border-b border-slate-50">
                      <td className="p-3">
                        <div className="font-medium">{d.full_name || d.email}</div>
                        <div className="text-xs text-slate-500">{d.email}</div>
                      </td>
                      <td className="p-3 text-xs text-slate-600">
                        {(d.duplicates || []).map((r, i) => (
                          <div key={i}>{r.collection} <span className="text-slate-400">·</span> {r.record_id}</div>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
